/******************************************************************************
 * Created Date: Tuesday September 15th 2020                                  *
 * Author: Sean W.                                                            *
 * -----                                                                      *
 * Last Modified: Tue Oct 27 2020                                             *
 * Modified By: Sean W.                                                       *
 * -----                                                                      *
 * File: /src/components/algorithms/frames/processImagesModal/page3/VarifyResults.js*
 * Copyright (c) 2020 MultiSensor                                             *
 ******************************************************************************/

import {
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from "@mui/icons-material";
import { CircularProgress, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React from "react";

import DetectionImage from "~/components/cameras/ProcessedFrames/components/DetectionImage";
import HorizontalScrollSwirs from "~/components/cameras/ProcessedFrames/components/ProcessedFramesTable/components/HorizontalScrollSwirs";

const PREFIX = "VarifyResults";

const classes = {
  baseholder: `${PREFIX}-baseholder`,
  arrowIcons: `${PREFIX}-arrowIcons`,
  arrowIconsDisabled: `${PREFIX}-arrowIconsDisabled`,
  fieldNames: `${PREFIX}-fieldNames`,
  fields: `${PREFIX}-fields`,
  helperTestClasses: `${PREFIX}-helperTestClasses`,
  imageInfo: `${PREFIX}-imageInfo`,
  infoFields: `${PREFIX}-infoFields`,
  mainHold: `${PREFIX}-mainHold`,
  mainImgRow: `${PREFIX}-mainImgRow`,
  multilineColor: `${PREFIX}-multilineColor`,
  showDetections: `${PREFIX}-showDetections`,
  noData: `${PREFIX}-noData`
};

const Root = styled("div")({
  [`.${classes.baseholder}`]: {
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    paddingTop: "40px",
    backgroundColor: "#171f2b"
  },
  [`.${classes.arrowIcons}`]: {
    fontSize: "80px",
    color: "white",
    opacity: "1"
  },
  [`.${classes.arrowIconsDisabled}`]: {
    fontSize: "80px",
    color: "white",
    opacity: ".4"
  },
  [`.${classes.fieldNames}`]: {
    width: "200px",
    display: "flex",
    flexDirection: "column"
  },
  [`.${classes.fields}`]: {
    width: "50px",
    marginRight: "50px",
    display: "flex",
    flexDirection: "column"
  },
  [`.${classes.helperTestClasses}`]: {
    margin: 4,
    color: "white"
  },
  [`.${classes.imageInfo}`]: {
    color: "#A0A2A6",
    fontSize: "14px",
    fontFamily: "Roboto",
    marginTop: "3%"
  },
  [`.${classes.infoFields}`]: {
    display: "flex",
    width: "300px"
  },
  [`.${classes.mainHold}`]: {
    position: "absolute",
    display: "contents",
    right: 0,
    left: 0,
    top: 0,
    bottom: 100
  },
  [`.${classes.mainImgRow}`]: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%", // "calc(100% - 160px)",
    minHeight: 400
  },
  [`.${classes.multilineColor}`]: {
    color: "white"
  },
  [`.${classes.showDetections}`]: {
    display: "flex",
    width: "100%"
  },
  [`.${classes.noData}`]: {
    marginLeft: "45%",
    marginTop: "20%"
  }
});

const VarifyResults = ({ scanResults }) => {
  const [currentSwirIndex, setCurrentSwirIndex] = React.useState(0);
  const [minThreshold, setMinThreshold] = React.useState(0);
  const [redThreshold, setRedThreshold] = React.useState(50);

  const setSwirById = id => {
    if (!id) {
      setCurrentSwirIndex(0);
    } else {
      for (let i = 0; i < scanResults.length; i++) {
        if (id === scanResults[i].id) {
          setCurrentSwirIndex(i);
        }
      }
    }
  };

  const backSelected = currentSwirIndex => {
    console.log(currentSwirIndex);
    if (currentSwirIndex > 0) {
      setCurrentSwirIndex(currentSwirIndex - 1);
    }
  };

  const forwardSelected = currentSwirIndex => {
    console.log(currentSwirIndex);
    if (currentSwirIndex < scanResults.length - 1) {
      setCurrentSwirIndex(currentSwirIndex + 1);
    }
  };

  const handleKeyPress = ({ keyCode }, currentSwirIndex) => {
    if (keyCode === 37) {
      backSelected(currentSwirIndex);
    } else if (keyCode === 39) {
      forwardSelected(currentSwirIndex);
    }
  };

  const handleMin = e => {
    setMinThreshold(e.target.value);
  };

  const handleRed = e => {
    setRedThreshold(e.target.value);
  };

  const isSelected = id => scanResults[currentSwirIndex].id === id;

  return (
    <Root
      className={classes.baseholder}
      onKeyDown={e => handleKeyPress(e, currentSwirIndex)}
    >
      {scanResults !== null ? (
        <React.Fragment>
          <div className={classes.mainHold}>
            <div className={classes.mainImgRow}>
              <KeyboardArrowLeftIcon
                className={
                  currentSwirIndex > 0
                    ? classes.arrowIcons
                    : classes.arrowIconsDisabled
                }
                onClick={() => backSelected(currentSwirIndex)}
              />
              {scanResults.length > 0 ? (
                <React.Fragment>
                  {scanResults[currentSwirIndex] && (
                    <DetectionImage
                      scanResult={scanResults[currentSwirIndex]}
                      showDetections={true}
                      redThreshold={redThreshold}
                      minThreshold={minThreshold}
                    />
                  )}
                  <div className={classes.infoFields}>
                    <div className={classes.fieldNames}>
                      <Typography className={classes.imageInfo}>
                        {" "}
                        MINIMUM THRESHOLD:{" "}
                      </Typography>
                      <Typography className={classes.imageInfo}>
                        {" "}
                        RED THRESHOLD:{" "}
                      </Typography>
                    </div>
                    <div className={classes.fields}>
                      <TextField
                        variant="standard"
                        id="minThreshold"
                        defaultValue={minThreshold}
                        onChange={handleMin}
                        type="number"
                        color="primary"
                        placeholder="Minimum threshold"
                        // helperText="Hit Enter To Apply Changes"
                        FormHelperTextProps={{
                          classes: { root: classes.helperTestClasses }
                        }}
                        // onChange={handleMinThreshold}
                        InputProps={{
                          classes: {
                            input: classes.multilineColor
                          }
                        }}
                      />
                      <TextField
                        variant="standard"
                        id="redThreshold"
                        defaultValue={redThreshold}
                        onChange={handleRed}
                        type="number"
                        color="primary"
                        placeholder="Red threshold"
                        FormHelperTextProps={{
                          classes: { root: classes.helperTestClasses }
                        }}
                        // onChange={handleMinThreshold}
                        InputProps={{
                          classes: {
                            input: classes.multilineColor
                          }
                        }}
                      />
                    </div>
                  </div>
                </React.Fragment>
              ) : (
                <>
                  <div> NO RESULTS </div>
                </>
              )}
              <KeyboardArrowRightIcon
                className={
                  currentSwirIndex < scanResults.length - 1
                    ? classes.arrowIcons
                    : classes.arrowIconsDisabled
                }
                onClick={() => forwardSelected(currentSwirIndex)}
              />
            </div>
          </div>

          <HorizontalScrollSwirs
            setSwirById={setSwirById}
            isSelected={isSelected}
            all={scanResults}
          />
        </React.Fragment>
      ) : (
        <CircularProgress className={classes.noData} />
      )}
    </Root>
  );
};

VarifyResults.propTypes = {
  submit: PropTypes.func,
  scanResults: PropTypes.array.isRequired
};

export default VarifyResults;
